import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';

import {
  IsCellReadOnly,
  mergeDraftIntoLive,
  TableConflict,
  TableConflictDecisions,
} from './mergeDraftIntoLive';

/** The table as the notebook has it now. */
export interface LiveTable {
  structuredTable: UnknownStructuredTable;
  locked: boolean;
  /** Writes the table to its current block in the entry. */
  write: (next: UnknownStructuredTable) => void;
}

/** One component's registration of a table, updated as the notebook's table changes. */
export interface LiveTableRegistration {
  update: (live: LiveTable) => void;
  unregister: () => void;
}

/**
 * The notebook's current state of each registered table, keyed by its document and `provenanceUuid`. A
 * component mounted in the notebook, such as a toolbar button, registers the table; an editor outside
 * the notebook's render tree, such as a `runModal` modal, reads it here. Keyed by table rather than held
 * by one component so that a remounted component, or a second view of the same table, keeps the entry
 * current.
 *
 * `provenanceUuid` alone isn't unique: entries created from the same template, and cloned entries,
 * share it.
 */
export class LiveTableRegistry {
  private entriesByKey = new Map<string, Array<{live: LiveTable}>>();

  private listeners = new Set<() => void>();

  /** The table's latest registration, or null when nothing registers it. */
  get(documentId: string | null, provenanceUuid: string): LiveTable | null {
    return this.entriesByKey.get(liveTableKey(documentId, provenanceUuid))?.at(-1)?.live ?? null;
  }

  register(documentId: string | null, provenanceUuid: string, live: LiveTable): LiveTableRegistration {
    const key = liveTableKey(documentId, provenanceUuid);
    const entry = {live};
    this.entriesByKey.set(key, [...(this.entriesByKey.get(key) ?? []), entry]);
    this.notify();
    return {
      update: (next) => {
        entry.live = next;
        this.notify();
      },
      unregister: () => {
        const remaining = (this.entriesByKey.get(key) ?? []).filter((e) => e !== entry);
        if (remaining.length > 0) {
          this.entriesByKey.set(key, remaining);
        } else {
          this.entriesByKey.delete(key);
        }
        this.notify();
      },
    };
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private notify(): void {
    this.listeners.forEach((listener) => {
      listener();
    });
  }
}

function liveTableKey(documentId: string | null, provenanceUuid: string): string {
  return `${documentId ?? ''}:${provenanceUuid}`;
}

/**
 * Why Save can't write at all, whatever the draft holds: the entry is locked, or nothing registers the
 * table, for example because it was deleted.
 */
export type SaveBlock = 'locked' | 'lost-context';

export function getSaveBlock(live: LiveTable | null): SaveBlock | null {
  if (live == null) {
    return 'lost-context';
  }
  return live.locked ? 'locked' : null;
}

export type CommitSaveResult =
  | {status: 'saved'}
  | {status: 'blocked'; block: SaveBlock}
  | {status: 'conflicts'; conflicts: Array<TableConflict>};

/**
 * Merges the draft into the live table and writes the result, or reports why it can't.
 *
 * Reading the live table and writing it must be one synchronous step: no `await`, promise, timer or
 * callback between `getLive()` and `write`, or the table can change in between and the write would
 * overwrite that change. Do any async work, such as a confirm dialog, before calling this.
 */
export function commitSave(
  base: UnknownStructuredTable,
  draft: UnknownStructuredTable,
  decisions: TableConflictDecisions,
  getLive: () => LiveTable | null,
  isCellReadOnly?: IsCellReadOnly
): CommitSaveResult {
  const live = getLive();
  if (live == null || live.locked) {
    return {status: 'blocked', block: live == null ? 'lost-context' : 'locked'};
  }
  const {next, conflicts} = mergeDraftIntoLive(base, draft, live.structuredTable, decisions, isCellReadOnly);
  if (conflicts.length > 0) {
    return {status: 'conflicts', conflicts};
  }
  if (next !== live.structuredTable) {
    live.write(next);
  }
  return {status: 'saved'};
}
